/** @jsxImportSource @emotion/react */
import React, { useCallback } from 'react'
import {
  CheckCircleOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { status } from '@sportninja/common/constants/attendanceStatus'
import {
  MyPlayer,
  UpcomingGame,
} from '@sportninja/common/types/PlayerAttendance'
import {
  emptySpaceStyle,
  attendanceQuestionTextStyle,
  attendanceOptionsStyle,
  attendanceButtonStyle,
  iconStyle,
} from '../styles'

/**
 * Props for the AttendanceQuestion component
 */
export interface AttendanceQuestionProps {
  /** List of players to display attendance buttons for */
  playersAttendance: MyPlayer[]
  /** Game data used for team name resolution */
  rowItem: UpcomingGame
  /** Callback when a player selects an attendance status */
  onAttendanceSelect: (params: {
    playerId: string
    gameId: string
    rosterId: string
    status: number
  }) => void
}

/**
 * Renders attendance selection buttons (YES / MAYBE / NO) for each player.
 * Highlights the currently selected status with color-coded borders.
 * Shows team name suffix when the user plays on multiple teams in the same game.
 *
 * @param {AttendanceQuestionProps} props - Component props
 * @returns {JSX.Element} Player attendance selection UI
 */
const AttendanceQuestion: React.FC<AttendanceQuestionProps> = ({
  playersAttendance,
  rowItem,
  onAttendanceSelect,
}) => {
  const handleAttendanceSelect = useCallback(
    (playerId: string, rosterId: string, selectedStatus: number) => {
      onAttendanceSelect({
        status: selectedStatus,
        playerId,
        rosterId,
        gameId: rowItem.id,
      })
    },
    [onAttendanceSelect, rowItem.id]
  )

  return (
    <>
      {playersAttendance.map((player, index) => {
        const isAttending = player?.attendance_status === status.ATTENDING
        const maybeAttending = player?.attendance_status === status.MAYBE
        const notAttending = player?.attendance_status === status.NOT_ATTENDING
        const teamName =
          playersAttendance.length > 1
            ? player?.team_id === rowItem?.home_team?.id
              ? `(${rowItem?.home_team?.name})`
              : `(${rowItem?.visiting_team?.name})`
            : ''
        const isLastItemOfList = index === playersAttendance.length - 1

        return (
          <React.Fragment key={player.id}>
            {playersAttendance?.length > 0 && isLastItemOfList ? (
              <div css={emptySpaceStyle} />
            ) : null}
            <p css={attendanceQuestionTextStyle}>
              Will you be attending next game? {teamName ? `${teamName}` : ''}
            </p>
            <div css={attendanceOptionsStyle}>
              <button
                css={attendanceButtonStyle(
                  isAttending ? 'transparent' : colors.SECONDARY_200,
                  isAttending ? colors.SUCCESS_200 : colors.NEUTRAL_100,
                  isAttending ? colors.SUCCESS_200 : colors.NEUTRAL_50
                )}
                onClick={() =>
                  handleAttendanceSelect(
                    player?.id,
                    player?.game_player_roster_id,
                    status.ATTENDING
                  )
                }
              >
                <CheckCircleOutlined
                  css={iconStyle(
                    isAttending ? colors.SUCCESS_200 : colors.NEUTRAL_50
                  )}
                />
                YES
              </button>
              <button
                css={attendanceButtonStyle(
                  maybeAttending ? 'transparent' : colors.SECONDARY_200,
                  maybeAttending ? colors.WARNING_200 : colors.NEUTRAL_100,
                  maybeAttending ? colors.WARNING_200 : colors.NEUTRAL_50
                )}
                onClick={() =>
                  handleAttendanceSelect(
                    player?.id,
                    player?.game_player_roster_id,
                    status.MAYBE
                  )
                }
              >
                <QuestionCircleOutlined
                  css={iconStyle(
                    maybeAttending ? colors.WARNING_200 : colors.NEUTRAL_50
                  )}
                />
                MAYBE
              </button>
              <button
                css={attendanceButtonStyle(
                  notAttending ? 'transparent' : colors.SECONDARY_200,
                  notAttending ? colors.ERROR_200 : colors.NEUTRAL_100,
                  notAttending ? colors.ERROR_200 : colors.NEUTRAL_50
                )}
                onClick={() =>
                  handleAttendanceSelect(
                    player?.id,
                    player?.game_player_roster_id,
                    status.NOT_ATTENDING
                  )
                }
              >
                <CloseCircleOutlined
                  css={iconStyle(
                    notAttending ? colors.ERROR_200 : colors.NEUTRAL_50
                  )}
                />
                NO
              </button>
            </div>
          </React.Fragment>
        )
      })}
    </>
  )
}

export default React.memo(AttendanceQuestion)
